import type { DocAttachment, DocDetail } from "./types.js";

import {
  BOTTOM,
  CONTENT_W,
  FAINT,
  INK,
  MARGIN,
  MUTED,
  NAVY,
  PAGE_H,
  PdfWriter,
  RULE,
  lineHeight,
  newA4Doc,
  parseMarkdownBlocks,
  sanitize,
} from "@/lib/pdf";

import { formatBytes } from "./utils.js";

/**
 * "Download PDF" for every documentation article.
 *
 * Every doc — Markdown article/guide/slide, FAQ, video, or an uploaded file —
 * yields a PDF, built client-side with the shared jsPDF vector engine
 * (`@/lib/pdf`). The one accepted exception is Office binaries (Word /
 * PowerPoint / Excel) and other un-renderable blobs: those can't be faithfully
 * converted in the browser, so they get a one-page COVER SHEET (title +
 * metadata + a pointer to the original file) while the native file download
 * stays available on the reader.
 *
 * `selectDocPdfContent` (pure) and `docPdfFilename` are unit-tested in
 * `docPdf.test.ts`; the render is exercised end-to-end in `documentation.cy.ts`.
 */

export type FileClass = "pdf" | "image" | "text" | "office" | "binary";

const OFFICE_EXT = ["doc", "docx", "ppt", "pptx", "xls", "xlsx"];
const OFFICE_CT = [
  "msword",
  "wordprocessingml",
  "ms-powerpoint",
  "presentationml",
  "ms-excel",
  "spreadsheetml",
];
// Only formats jsPDF can reliably embed raster bytes for. gif/webp/bmp/svg
// fall through to the cover sheet rather than risk a broken/blank page.
const IMAGE_EXT = ["png", "jpg", "jpeg"];
const TEXT_EXT = ["txt", "md", "markdown", "csv", "tsv", "log", "json", "yml", "yaml", "xml"];

function ext(filename: string): string {
  const m = filename.toLowerCase().match(/\.([a-z0-9]+)$/);
  return m?.[1] ?? "";
}

/** Classify an attachment so the builder knows whether it can render a true
 * PDF (pdf passthrough / image embed / text render) or must fall back to a
 * cover sheet (Office binaries + anything else un-renderable). */
export function classifyFile(file: Pick<DocAttachment, "filename" | "content_type">): FileClass {
  const e = ext(file.filename);
  const ct = (file.content_type ?? "").toLowerCase();

  if (OFFICE_EXT.includes(e) || OFFICE_CT.some((s) => ct.includes(s))) return "office";
  if (ct === "application/pdf" || e === "pdf") return "pdf";
  if (IMAGE_EXT.includes(e) || ct === "image/png" || ct === "image/jpeg") return "image";
  if (
    TEXT_EXT.includes(e) ||
    ct.startsWith("text/") ||
    ct === "application/json" ||
    ct === "application/xml"
  ) {
    return "text";
  }
  return "binary";
}

export type DocPdfMode =
  | "markdown"
  | "faq"
  | "video"
  | "image"
  | "text"
  | "pdf-passthrough"
  | "cover"
  | "empty";

export interface DocPdfPlan {
  mode: DocPdfMode;
  /** The primary attachment, for the file-backed modes. */
  file?: DocAttachment;
}

/**
 * Decide what a doc's PDF should contain. Mirrors the on-screen reader's
 * precedence: a Markdown body wins, then FAQ items, then the video payload,
 * then the first attachment (classified), else nothing.
 */
export function selectDocPdfContent(detail: DocDetail): DocPdfPlan {
  if (detail.content && detail.content.trim().length > 0) return { mode: "markdown" };
  if (detail.doc_type === "faq" && (detail.faq_items?.length ?? 0) > 0) return { mode: "faq" };
  if (detail.doc_type === "video" && detail.video) return { mode: "video" };

  const file = detail.files[0];
  if (file) {
    const klass = classifyFile(file);
    if (klass === "pdf") return { mode: "pdf-passthrough", file };
    if (klass === "image") return { mode: "image", file };
    if (klass === "text") return { mode: "text", file };
    return { mode: "cover", file }; // office + other binaries
  }
  return { mode: "empty" };
}

/** `<slug>.pdf` (safe filename) for the rendered download. */
export function docPdfFilename(detail: Pick<DocDetail, "slug" | "title">): string {
  const base = (detail.slug || detail.title || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);
  return base ? `${base}.pdf` : "document.pdf";
}

export interface BuildDocPdfDeps {
  /** Fetch the raw bytes of the doc's primary attachment through the
   * dashboard origin (server passthrough) — so images/PDFs aren't CORS-tainted
   * and the desktop shell can read them. Only called for the file-backed
   * render modes (image / text / pdf-passthrough). */
  fetchFileBytes: (
    docId: number,
  ) => Promise<{ bytes: ArrayBuffer; contentType: string | null }>;
}

export interface DocPdfResult {
  blob: Blob;
  filename: string;
  /** True when the blob IS the original PDF (passthrough), not a re-render. */
  passthrough: boolean;
}

function ensurePdfName(name: string): string {
  return /\.pdf$/i.test(name) ? name : `${name}.pdf`;
}

/**
 * Build the downloadable PDF for a document. Async because the file-backed
 * modes fetch bytes through the server passthrough.
 */
export async function buildDocPdf(
  detail: DocDetail,
  deps: BuildDocPdfDeps,
): Promise<DocPdfResult> {
  const plan = selectDocPdfContent(detail);

  // Already a PDF → hand back the original bytes untouched (no re-render).
  if (plan.mode === "pdf-passthrough" && plan.file) {
    const { bytes } = await deps.fetchFileBytes(detail.id);
    return {
      blob: new Blob([bytes], { type: "application/pdf" }),
      filename: ensurePdfName(plan.file.filename || docPdfFilename(detail)),
      passthrough: true,
    };
  }

  const w = new PdfWriter(newA4Doc());
  writeHeader(w, detail);

  switch (plan.mode) {
    case "markdown":
      renderMarkdown(w, detail.content ?? "");
      break;
    case "faq":
      renderFaq(w, detail);
      break;
    case "video":
      renderVideo(w, detail);
      break;
    case "image":
      await renderImage(w, detail, plan.file, deps);
      break;
    case "text":
      await renderText(w, detail, plan.file, deps);
      break;
    case "cover":
      renderCover(w, plan.file);
      break;
    case "empty":
    default:
      w.write("This document has no body content yet.", { size: 11, color: MUTED });
      break;
  }

  w.addPageFooters(detail.title || "Document");
  return {
    blob: w.doc.output("blob"),
    filename: docPdfFilename(detail),
    passthrough: false,
  };
}

// ── section builders ───────────────────────────────────────────────────────

function writeHeader(w: PdfWriter, detail: DocDetail): void {
  w.write(detail.title || "Untitled document", { size: 18, style: "bold", color: NAVY });
  w.gap(1);
  const meta = [
    detail.category?.name ?? null,
    detail.default_release ? `v${detail.default_release.version}` : null,
    statusLabel(detail.status),
    detail.author?.name ?? null,
    `Updated ${formatDate(detail.updated_at)}`,
  ].filter(Boolean) as string[];
  w.write(meta.join("  ·  "), { size: 9.5, color: MUTED });
  if (detail.summary && detail.summary.trim()) {
    w.gap(0.5);
    w.write(detail.summary.trim(), { size: 10, style: "italic", color: MUTED });
  }
  w.gap(0.5);
  w.write(`Exported from the XOP Networks reseller portal · ${new Date().toLocaleString()}`, {
    size: 8,
    color: FAINT,
  });
  w.gap(2);
}

/** Full-grammar markdown → paginated vector text (matches the on-screen
 * `Markdown` reader: h1–3, paragraphs, lists, fenced code, rules, inline). */
function renderMarkdown(w: PdfWriter, src: string): void {
  const doc = w.doc;
  for (const b of parseMarkdownBlocks(src)) {
    if (b.kind === "h") {
      const size = b.level === 1 ? 15 : b.level === 2 ? 12.5 : 11;
      w.gap(b.level === 1 ? 3 : 2);
      w.write(b.text, { size, style: "bold", color: NAVY });
      w.gap(0.75);
    } else if (b.kind === "li") {
      const indent = 5;
      const lh = lineHeight(10.5);
      const lines = (doc.setFont("helvetica", "normal"), doc.setFontSize(10.5),
        doc.splitTextToSize(sanitize(b.text), CONTENT_W - indent) as string[]);
      lines.forEach((ln, idx) => {
        w.need(lh);
        w.y += lh;
        doc.setFont("helvetica", "normal");
        doc.setFontSize(10.5);
        if (idx === 0) {
          w.setColor(MUTED);
          doc.text(b.ordinal, MARGIN, w.y);
        }
        w.setColor(INK);
        doc.text(ln, MARGIN + indent, w.y);
      });
      w.gap(0.75);
    } else if (b.kind === "code") {
      // Preformatted block: courier, line-by-line (no wrap-join), light rule.
      const lh = lineHeight(9);
      for (const raw of b.text.split("\n")) {
        const lines = (doc.setFont("courier", "normal"), doc.setFontSize(9),
          doc.splitTextToSize(sanitize(raw) || " ", CONTENT_W - 4) as string[]);
        for (const ln of lines) {
          w.need(lh);
          w.y += lh;
          doc.setFont("courier", "normal");
          doc.setFontSize(9);
          w.setColor(MUTED);
          doc.text(ln, MARGIN + 2, w.y);
        }
      }
      w.gap(1.5);
    } else if (b.kind === "hr") {
      w.gap(2);
      w.need(2);
      doc.setDrawColor(RULE[0], RULE[1], RULE[2]);
      doc.setLineWidth(0.2);
      doc.line(MARGIN, w.y, MARGIN + CONTENT_W, w.y);
      w.gap(2);
    } else {
      w.write(b.text, { size: 10.5 });
      w.gap(1.5);
    }
  }
}

function renderFaq(w: PdfWriter, detail: DocDetail): void {
  for (const item of detail.faq_items ?? []) {
    w.gap(1);
    w.write(item.question, { size: 11.5, style: "bold", color: NAVY });
    w.gap(0.5);
    renderMarkdown(w, item.answer_markdown);
    w.gap(1);
  }
}

function renderVideo(w: PdfWriter, detail: DocDetail): void {
  const video = detail.video;
  w.section("Video");
  const bits = [
    video?.kind ? `Type: ${video.kind}` : null,
    video?.duration ? `Duration: ${formatDuration(video.duration)}` : null,
  ].filter(Boolean) as string[];
  if (bits.length > 0) {
    w.write(bits.join("  ·  "), { size: 10, color: MUTED });
    w.gap(1);
  }
  // Prefer the human-facing source page over the normalized player/file
  // URLs (an /embed/ or dl.dropboxusercontent.com link looks odd on paper).
  const watchUrl = detail.external_url ?? video?.provider_url ?? video?.direct_url ?? null;
  if (watchUrl) {
    w.write(`Watch: ${watchUrl}`, { size: 10.5, color: INK });
    w.gap(1);
  }
  // A video can't live inside a PDF — the link above is the watchable copy.
  // Any written notes/transcript live in the body content (rendered above).
  if (!watchUrl) {
    w.write(
      "This video has no shareable link. Open the document in the portal to watch it.",
      { size: 10, style: "italic", color: MUTED },
    );
  }
}

async function renderImage(
  w: PdfWriter,
  detail: DocDetail,
  file: DocAttachment | undefined,
  deps: BuildDocPdfDeps,
): Promise<void> {
  if (!file) {
    renderCover(w, file);
    return;
  }
  try {
    const { bytes, contentType } = await deps.fetchFileBytes(detail.id);
    const u8 = new Uint8Array(bytes);
    const ct = (contentType ?? file.content_type ?? "").toLowerCase();
    const fmt = ct.includes("png") || ext(file.filename) === "png" ? "PNG" : "JPEG";
    const props = w.doc.getImageProperties(u8);
    const maxW = CONTENT_W;
    const maxH = PAGE_H - MARGIN - w.y - 6;
    const scale = Math.min(maxW / props.width, maxH / props.height, 1);
    const drawW = props.width * scale;
    const drawH = props.height * scale;
    w.need(drawH + 2);
    w.y += 2;
    w.doc.addImage(u8, fmt, MARGIN, w.y, drawW, drawH);
    w.y += drawH;
    w.gap(2);
    w.write(`${file.filename} · ${formatBytes(file.byte_size)}`, { size: 8, color: FAINT });
  } catch {
    // Couldn't decode/embed — fall back to a cover sheet so the click still
    // yields a usable PDF instead of throwing.
    renderCover(w, file);
  }
}

async function renderText(
  w: PdfWriter,
  detail: DocDetail,
  file: DocAttachment | undefined,
  deps: BuildDocPdfDeps,
): Promise<void> {
  if (!file) {
    renderCover(w, file);
    return;
  }
  try {
    const { bytes } = await deps.fetchFileBytes(detail.id);
    const text = new TextDecoder("utf-8", { fatal: false }).decode(bytes);
    // Markdown/CSV/plain — render as Markdown for .md, else preformatted.
    if (/\.(md|markdown)$/i.test(file.filename)) {
      renderMarkdown(w, text);
    } else {
      const doc = w.doc;
      const lh = lineHeight(9);
      for (const raw of text.split("\n")) {
        const lines = (doc.setFont("courier", "normal"), doc.setFontSize(9),
          doc.splitTextToSize(sanitize(raw) || " ", CONTENT_W) as string[]);
        for (const ln of lines) {
          w.need(lh);
          w.y += lh;
          doc.setFont("courier", "normal");
          doc.setFontSize(9);
          w.setColor(INK);
          doc.text(ln, MARGIN, w.y);
        }
      }
    }
  } catch {
    renderCover(w, file);
  }
}

/** One-page cover sheet for Office binaries + anything else un-renderable.
 * The doc header is already drawn; this adds the "download the original" note. */
function renderCover(w: PdfWriter, file: DocAttachment | undefined): void {
  w.section("Original file");
  if (file) {
    w.write(file.filename, { size: 11, style: "bold", color: INK });
    w.write(`${file.content_type ?? "unknown type"} · ${formatBytes(file.byte_size)}`, {
      size: 9.5,
      color: MUTED,
    });
    w.gap(1.5);
  }
  w.write(
    "This document's source is a file that can't be rendered into a PDF in the browser " +
      "(e.g. a Word, PowerPoint, or Excel document). This page is a cover summary — " +
      "open the document in the portal and use Download to get the original file.",
    { size: 10, color: MUTED },
  );
}

// ── small formatters ─────────────────────────────────────────────────────

function statusLabel(status: string): string {
  switch (status) {
    case "published": return "Published";
    case "draft": return "Draft";
    case "in_review": return "In review";
    default: return status;
  }
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString();
}

function formatDuration(seconds: number): string {
  const s = Math.max(0, Math.round(seconds));
  const m = Math.floor(s / 60);
  const rem = s % 60;
  return `${m}:${String(rem).padStart(2, "0")}`;
}
